<script setup lang="ts">
import { computed } from 'vue'

import BetaSettingsBeforeAfter from '@/components/beta-editor/settings/BetaSettingsBeforeAfter.vue'
import BetaSettingsButton from '@/components/beta-editor/settings/BetaSettingsButton.vue'
import BetaSettingsContainer from '@/components/beta-editor/settings/BetaSettingsContainer.vue'
import BetaSettingsDefault from '@/components/beta-editor/settings/BetaSettingsDefault.vue'
import BetaSettingsForm from '@/components/beta-editor/settings/BetaSettingsForm.vue'
import BetaSettingsGallery from '@/components/beta-editor/settings/BetaSettingsGallery.vue'
import BetaSettingsImage from '@/components/beta-editor/settings/BetaSettingsImage.vue'
import BetaSettingsRow from '@/components/beta-editor/settings/BetaSettingsRow.vue'
import BetaSettingsSection from '@/components/beta-editor/settings/BetaSettingsSection.vue'
import BetaSettingsText from '@/components/beta-editor/settings/BetaSettingsText.vue'
import BetaSettingsTitle from '@/components/beta-editor/settings/BetaSettingsTitle.vue'
import { normalizeBetaSidebarReference } from '@/components/beta-editor/sidebar-reference'
import { useEditorBetaStore } from '@/stores/editor/beta'

const betaStore = useEditorBetaStore()

const resolvedReference = computed(() => {
    if (betaStore.sidebarReference === 'section' && betaStore.sectionForEdit) {
        return 'section'
    }

    if (betaStore.elementForEdit?.frameType) {
        return normalizeBetaSidebarReference(betaStore.sidebarReference || betaStore.elementForEdit.frameType)
    }

    return null
})

const currentComponent = computed(() => {
    switch (resolvedReference.value) {
        case 'section':
            return BetaSettingsSection
        case 'title':
            return BetaSettingsTitle
        case 'text':
            return BetaSettingsText
        case 'gallery':
            return BetaSettingsGallery
        case 'button':
            return BetaSettingsButton
        case 'image':
            return BetaSettingsImage
        case 'container':
            return BetaSettingsContainer
        case 'row':
            return BetaSettingsRow
        case 'form':
            return BetaSettingsForm
        case 'beforeAfterImage':
            return BetaSettingsBeforeAfter
        default:
            return BetaSettingsDefault
    }
})
</script>

<template>
    <aside class="beta-editor-sidebar">
        <div class="beta-editor-sidebar__content">
            <component :is="currentComponent" />
        </div>
    </aside>
</template>

<style scoped>
.beta-editor-sidebar {
    @apply h-full w-full overflow-y-auto px-5 py-6;
    scrollbar-gutter: stable;
}

.beta-editor-sidebar__content {
    @apply flex min-h-full w-full min-w-0 flex-col gap-5 pb-10;
}

.beta-editor-sidebar__content > :deep(div[class*='bg-[#E0E0E0]'][class*='p-1']) {
    @apply rounded-2xl border border-[#E4EAF3] bg-[#EEF2F8] p-1.5 shadow-sm;
}

.beta-editor-sidebar__content > :deep(div[class*='bg-[#E0E0E0]'][class*='p-1'] button) {
    @apply min-h-[42px] rounded-xl;
}

.beta-editor-sidebar__content > :deep([id^='headlessui-disclosure-button']) {
    @apply rounded-2xl border border-[#E4EAF3] bg-white px-4 py-3.5 shadow-sm transition-colors duration-200;
}

.beta-editor-sidebar__content > :deep([id^='headlessui-disclosure-button']:hover) {
    @apply border-[#D4DEEC] bg-[#FBFCFF];
}

.beta-editor-sidebar__content > :deep([id^='headlessui-disclosure-button'][data-headlessui-state='open']) {
    @apply rounded-b-none border-b-transparent shadow-none;
}

.beta-editor-sidebar__content > :deep([id^='headlessui-disclosure-button'] > span) {
    @apply text-[15px] leading-5 text-[#10233E];
}

.beta-editor-sidebar__content > :deep([id^='headlessui-disclosure-button'] > button) {
    @apply flex h-7 w-7 items-center justify-center rounded-full bg-[#F5F7FB];
}

.beta-editor-sidebar__content > :deep([id^='headlessui-disclosure-panel']) {
    @apply -mt-5 rounded-b-2xl border-x border-b border-[#E4EAF3] bg-white px-4 pb-5 pt-1 shadow-sm;
}

.beta-editor-sidebar__content > :deep([id^='headlessui-disclosure-panel'] > * + *) {
    @apply mt-5;
}

.beta-editor-sidebar__content > :deep(.beta-settings-panel) {
    @apply rounded-2xl border border-[#E4EAF3] bg-white p-4 shadow-sm;
}

.beta-editor-sidebar__content > :deep(.beta-settings-panel h2) {
    @apply border-b border-[#EEF2F8] pb-3 text-[15px] leading-5;
}

.beta-editor-sidebar__content > :deep(.beta-settings-empty) {
    @apply rounded-2xl border-[#DCE5F2] bg-white p-5 shadow-sm;
}

.beta-editor-sidebar__content :deep(.space-y-2) {
    @apply space-y-3;
}

.beta-editor-sidebar__content :deep(label),
.beta-editor-sidebar__content :deep(.space-y-2 > span:first-child),
.beta-editor-sidebar__content :deep(.flex > span.text-sm.font-semibold) {
    @apply leading-5 text-[#6B7280] opacity-100;
}

.beta-editor-sidebar__content :deep(input[type='text']:not(.beta-color-picker__input)),
.beta-editor-sidebar__content :deep(input[type='number']),
.beta-editor-sidebar__content :deep(select) {
    @apply min-h-[42px] rounded-xl border border-[#E4EAF3] bg-[#FBFCFF] px-3 text-sm text-[#10233E] transition-colors duration-200;
}

.beta-editor-sidebar__content :deep(input[type='text']:not(.beta-color-picker__input):focus),
.beta-editor-sidebar__content :deep(input[type='number']:focus),
.beta-editor-sidebar__content :deep(select:focus) {
    @apply border-[var(--main-color)] bg-white;
}

.beta-editor-sidebar__content :deep(.flex:has(> [class~='w-[3rem]']) > svg) {
    @apply mx-0;
}

.beta-editor-sidebar__content :deep([class~='w-[3rem]'] > input[type='number']) {
    @apply min-w-0 rounded-none border-0 bg-transparent px-0 text-[11px];
    appearance: auto;
}

.beta-editor-sidebar__content :deep([class~='w-[3rem]'] > input[type='number']::-webkit-inner-spin-button) {
    width: 12px;
    margin: 0;
    opacity: 1;
}

.beta-editor-sidebar__content :deep(input[type='range']) {
    @apply min-h-0;
}

.beta-editor-sidebar__content :deep(.text-sidebar-toolbar),
.beta-editor-sidebar__content :deep(.gallery-empty-state) {
    @apply mt-4;
}

.beta-editor-sidebar__content :deep(.gallery-action) {
    @apply mb-4;
}
</style>
